import { IS_PRODUCTION } from "@app/config/getServiceUrl";
import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { cn, isAxiosApiError } from "@base/lib/utils";
import { DOCUMENT_TYPE } from "@base/model/constants";
import { useDeleteConfirmDialog, useProjectAnalyticsGroup } from "@base/model/hooks";
import { useAppNavigate } from "@base/model/hooks/useAppNavigate";
import { useCurrentUserQuery } from "@base/model/hooks/useCurrentUserQuery";
import { useDeleteDocumentMutation } from "@base/model/hooks/useDocumentsQuery";
import { useBottomSheet } from "@base/ui/BottomSheet";
import { Image } from "@base/ui/Image";
import { PullToRefreshIndicator } from "@base/ui/PullToRefreshScrollView/PullToRefreshIndicator";
import { usePullToRefresh } from "@base/ui/PullToRefreshScrollView/usePullToRefresh";
import { styles as richTextStyles } from "@base/ui/RichTextEditor";
import { VirtualizedImageGrid } from "@base/ui/VirtualizedImageGrid";
import { Button, Chip, Dropdown } from "@buildblockinc/buildblock-design-system";
import { DownloadImagesButton } from "@features/download-images";
import { WEATHER_GROUP_MAP, type WeatherCondition } from "@features/write-daily-log/ui/steps/WeatherSelector";
import { ErrorBoundary } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import type { HistoryState } from "@tanstack/react-router";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import { ChevronLeft, ChevronRight, EllipsisVertical, FileX, MessageSquareText, X } from "lucide-react";
import { Fragment, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import type { DailyLogDetailResponse, DailyLogResource } from "../model/schema";
import { dailyLogDetailQueryOptions, useDailyLogDetailQuery } from "../model/useDailyLogDetailQuery";
import { useAcquireLockMutation } from "../model/useDailyLogLockMutations";
import { dailyLogLockStatusQueryOptions, useDailyLogLockStatusQuery } from "../model/useDailyLogLockStatusQuery";
import { useDailyLogShareTokenQuery } from "../model/useDailyLogShareTokenQuery";
import { CommentsBottomSheet } from "./CommentsBottomSheet";
import { FileHistoryBottomSheet } from "./FileHistoryBottomSheet";

function formatDDay(dDay: string): string | null {
  const num = Number(dDay);
  if (!Number.isFinite(num)) return null;
  if (num === 0) return "D-Day";
  return `D${num > 0 ? "+" : ""}${num}`;
}

function withEmptyFallback(value: string, fallback = "—"): { value: string; muted: boolean } {
  const trimmed = value.trim();
  return trimmed ? { value: trimmed, muted: false } : { value: fallback, muted: true };
}

type SummaryItem = {
  id: string | number;
  label: string;
  value: string | number;
};

function SummarySection({ title, items, totalValue }: { title: string; items: SummaryItem[]; totalValue?: number }) {
  if (!items || items.length === 0) return null;

  return (
    <section className="flex flex-col mb-6 md:mb-0 md:bg-white md:rounded-8 md:border md:border-gray-200 md:overflow-hidden">
      <h3 className="mb-3 text-gray-1000 text-subtitle4 md:mb-0 md:px-4 md:py-3 md:bg-gray-10 md:border-b md:border-gray-200">
        {title}
      </h3>
      <div className="flex flex-col flex-1 gap-y-3 p-4 bg-gray-10 rounded-8 md:gap-y-0 md:p-0 md:bg-white md:rounded-none">
        {items.map((item, index) => (
          <Fragment key={item.id}>
            <div className="flex justify-between items-center text-gray-900 text-label2 md:px-4 md:py-4">
              <span>{item.label}</span>
              <span>{item.value}</span>
            </div>
            {index < items.length - 1 && <div className="border-b border-gray-300 md:border-gray-100" />}
          </Fragment>
        ))}
        {!!totalValue && (
          <>
            <div className="border-b border-gray-300 md:border-gray-200" />
            <div className="flex justify-between items-center mt-3 text-black text-subtitle4 md:mt-0 md:px-4 md:py-3">
              <span>Total</span>
              <span>{totalValue}</span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

type ResourceGroup = {
  contractor_name: string;
  trades: Array<{ trade_id: number; trade_name: string; quantity: number }>;
};

function groupResourceByContractor(items: DailyLogResource[]): ResourceGroup[] {
  const map = new Map<number, ResourceGroup>();
  for (const item of items) {
    if (!map.has(item.contractor_id)) {
      map.set(item.contractor_id, { contractor_name: item.contractor_name, trades: [] });
    }
    map.get(item.contractor_id)!.trades.push({
      trade_id: item.trade_id,
      trade_name: item.trade_name,
      quantity: item.quantity,
    });
  }
  return Array.from(map.values());
}

function ResourceSummarySection({
  title,
  groups,
  totalQuantity,
}: {
  title: string;
  groups: ResourceGroup[];
  totalQuantity: number;
}) {
  if (groups.length === 0) return null;

  return (
    <section className="flex flex-col mb-6 md:mb-0 md:bg-white md:rounded-8 md:border md:border-gray-200 md:overflow-hidden">
      <h3 className="flex gap-x-1 items-center mb-3 text-gray-1000 text-subtitle4 md:mb-0 md:px-4 md:py-3 md:bg-gray-10 md:border-b md:border-gray-200">
        <span>{title}</span>
        <span className="hidden font-normal text-gray-600 text-label3 md:inline">
          · {groups.length} contractor{groups.length > 1 ? "s" : ""}
        </span>
      </h3>
      <div className="flex flex-col flex-1 gap-6 p-4 bg-gray-10 rounded-8 md:gap-0 md:p-0 md:bg-white md:rounded-none">
        <div className="flex flex-col gap-4 md:gap-0">
          {groups.map(({ contractor_name, trades }) => {
            const contractorTotal = trades.reduce((sum, trade) => sum + trade.quantity, 0);
            return (
              <div key={contractor_name} className="flex flex-col gap-1 md:gap-0">
                <div className="flex justify-between items-center text-gray-900 text-subtitle4 md:px-4 md:pt-3 md:pb-1.5">
                  <span>{contractor_name}</span>
                  <span className="hidden font-normal text-gray-600 text-label3 md:inline">{contractorTotal}</span>
                </div>
                <div className="flex gap-4 py-2 pr-2 pl-4 md:py-0 md:pr-4 md:pl-7">
                  <div className="self-stretch w-0 border-l border-gray-300 shrink-0" />
                  <div className="flex flex-col flex-1 min-w-0">
                    {trades.map((trade, index) => (
                      <div key={trade.trade_id}>
                        {index > 0 && <div className="my-3 h-px bg-gray-300 md:my-0 md:border-b md:border-gray-100" />}
                        <div className="flex justify-between items-center text-gray-900 text-label2 md:py-[7px]">
                          <span>{trade.trade_name}</span>
                          <span>{trade.quantity}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between items-center pr-1 text-subtitle4 text-gray-1000 md:px-4 md:py-3 md:border-t md:border-gray-200 md:mt-2">
          <span>Total</span>
          <span>{totalQuantity}</span>
        </div>
      </div>
    </section>
  );
}

/** 컨테이너 폭은 shell이 1200px로 제한하므로 열 수는 breakpoint로만 정한다. */
const PHOTO_GRID_BREAKPOINTS = [
  { query: "(min-width: 1024px)", columns: 4 },
  { query: "(min-width: 768px)", columns: 3 },
] as const;
const DEFAULT_PHOTO_GRID_COLUMNS = 2;

function getPhotoGridColumns(): number {
  if (typeof window === "undefined" || !window.matchMedia) return DEFAULT_PHOTO_GRID_COLUMNS;
  const matched = PHOTO_GRID_BREAKPOINTS.find((bp) => window.matchMedia(bp.query).matches);
  return matched?.columns ?? DEFAULT_PHOTO_GRID_COLUMNS;
}

function usePhotoGridColumns(): number {
  const [columns, setColumns] = useState(getPhotoGridColumns);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mediaQueryLists = PHOTO_GRID_BREAKPOINTS.map((bp) => window.matchMedia(bp.query));
    const handleChange = () => setColumns(getPhotoGridColumns());
    for (const mql of mediaQueryLists) mql.addEventListener("change", handleChange);
    handleChange();
    return () => {
      for (const mql of mediaQueryLists) mql.removeEventListener("change", handleChange);
    };
  }, []);

  return columns;
}

export function useDailyLogDisplayData(data: DailyLogDetailResponse) {
  const overviewItems = useMemo(() => {
    const items: SummaryItem[] = data.tags.map((tag) => ({ id: `tag-${tag.id}`, label: tag.name, value: "Yes" }));

    const processRate = Number(data.process_rate);
    if (data.process_rate.trim() !== "" && Number.isFinite(processRate)) {
      items.push({ id: "process-rate", label: "Process Rate", value: `${processRate}%` });
    }

    return items;
  }, [data.tags, data.process_rate]);

  const workforceGroups = useMemo(() => groupResourceByContractor(data.workforces.items), [data.workforces.items]);

  const equipmentGroups = useMemo(() => groupResourceByContractor(data.equipments.items), [data.equipments.items]);

  const weatherLabel = WEATHER_GROUP_MAP[data.weather as WeatherCondition] ?? data.weather;
  const formattedWeather = weatherLabel.charAt(0).toUpperCase() + weatherLabel.slice(1).toLowerCase();

  const infoItems = useMemo(() => {
    const location = withEmptyFallback(data.location);
    const parsedDate = data.date.trim() ? dayjs(data.date) : null;
    const isValidDate = parsedDate?.isValid() ?? false;
    const weather = withEmptyFallback(data.weather);
    const precipitation = withEmptyFallback(data.precipitation);

    return [
      {
        icon: "/assets/images/icons/location.svg",
        label: "Project location",
        value: location.value,
        muted: location.muted,
      },
      {
        icon: "/assets/images/icons/calendar.svg",
        label: "Date",
        value: parsedDate?.isValid() ? parsedDate.format("MMMM D, YYYY") : "—",
        muted: !isValidDate,
        chip: isValidDate ? formatDDay(data.d_day) : null,
      },
      {
        icon: "/assets/images/icons/person.svg",
        label: "Writer",
        value: data.user?.name ?? "Unknown writer",
        muted: !data.user,
      },
      {
        icon: "/assets/images/icons/weather.svg",
        label: "Weather",
        value: weather.muted ? weather.value : `${formattedWeather}, ${data.temperature} ${data.temperature_unit}`,
        muted: weather.muted,
      },
      {
        icon: "/assets/images/icons/wet.svg",
        label: "Precipitation",
        value: precipitation.muted
          ? precipitation.value
          : `${precipitation.value} ${data.precipitation_unit.toLowerCase()}`,
        muted: precipitation.muted,
      },
    ];
  }, [data, formattedWeather]);

  return {
    overviewItems,
    workforceGroups,
    workforceTotalQuantity: data.workforces.total_quantity,
    equipmentGroups,
    equipmentTotalQuantity: data.equipments.total_quantity,
    infoItems,
  };
}

interface DailyLogDetailContentProps {
  projectId: string;
  logId: string;
  onClose: () => void;
  /**
   * 편집 화면으로 이동할 때 이어 줄 history state. 상세로 들어올 때 받은 복귀 정보를
   * 편집→저장/취소→상세까지 유지하기 위해 page가 넘긴다. 이 feature는 내용을 해석하지 않는다.
   */
  editNavigationState?: HistoryState;
  /** 삭제 성공 후, 닫기 전에 page가 추가로 할 일 (예: 목록 캐시 무효화) */
  onDeleted?: () => void;
}

function DailyLogDetailContentSkeleton() {
  return (
    <div className="flex flex-col animate-pulse h-dvh">
      <header className="flex sticky top-0 z-10 gap-x-2 items-center p-4 md:px-0 bg-gray-10">
        <div className="w-5 h-5 rounded-4 skeleton-shimmer shrink-0" />
        <div className="flex-1 h-5 rounded-4 skeleton-shimmer" />
        <div className="w-5 h-5 rounded-4 skeleton-shimmer shrink-0" />
        <div className="w-5 h-5 rounded-4 skeleton-shimmer shrink-0" />
      </header>
      <main className="overflow-y-auto flex-1 p-4 md:px-0 bg-white scrollbar-hide">
        <div className="md:bg-white md:rounded-8 md:border md:border-gray-200">
          <div className="mb-8 md:mb-0 md:px-7 md:py-6 md:border-b md:border-gray-200">
            <div className="mb-6 w-48 h-6 rounded-4 skeleton-shimmer" />
            <ul className="flex flex-col gap-y-4 md:grid md:gap-x-6 md:gap-y-4 md:[grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
              {Array.from({ length: 5 }, (_, i) => `info-skeleton-${i}`).map((key) => (
                <li key={key} className="flex gap-x-4 items-center">
                  <div className="w-6 h-6 rounded-4 skeleton-shimmer shrink-0" />
                  <div className="flex flex-col gap-1">
                    <div className="w-20 h-3 rounded-4 skeleton-shimmer" />
                    <div className="w-40 h-4 rounded-4 skeleton-shimmer" />
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="mb-6 md:mb-0 md:grid md:grid-cols-2 md:gap-6 md:px-7 md:py-6 md:border-b md:border-gray-200 lg:grid-cols-3">
            <div>
              <div className="mb-3 w-24 h-5 rounded-4 skeleton-shimmer" />
              <div className="flex flex-col gap-y-3 p-4 bg-gray-10 rounded-8">
                <div className="h-4 rounded-4 skeleton-shimmer" />
                <div className="h-4 rounded-4 skeleton-shimmer" />
              </div>
            </div>
          </div>
          <div className="mb-6 md:mb-0 md:px-7 md:py-6 md:border-b md:border-gray-200">
            <div className="mb-3 w-24 h-5 rounded-4 skeleton-shimmer" />
            <div className="h-24 rounded-8 skeleton-shimmer" />
          </div>
          <div className="md:px-7 md:py-6">
            <div className="mb-3 w-24 h-5 rounded-4 skeleton-shimmer" />
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {Array.from({ length: 4 }, (_, i) => `img-skeleton-${i}`).map((key) => (
                <div key={key} className="aspect-square rounded-8 skeleton-shimmer" />
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

interface DailyLogDetailContentErrorProps {
  projectId: string;
  logId: string;
  onRetry: () => void;
  onClose: () => void;
}

function DailyLogDetailContentError({ projectId, logId, onRetry, onClose }: DailyLogDetailContentErrorProps) {
  const queryClient = useQueryClient();

  const handleRetry = () => {
    queryClient.resetQueries({ queryKey: dailyLogDetailQueryOptions(projectId, logId).queryKey });
    onRetry();
  };

  return (
    <div className="flex flex-col bg-white h-dvh">
      <div className="flex flex-col flex-1 gap-6 justify-center items-center px-8 pb-20">
        <div className="flex justify-center items-center w-20 h-20 rounded-circle bg-red-10">
          <FileX width={38} height={38} className="text-red-500" strokeWidth={1.5} />
        </div>

        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-subtitle3 text-gray-1000">Report Unavailable</h1>
          <p className="leading-relaxed text-gray-600 text-body3">
            We couldn't load this construction report.
            <br />
            Please check your connection and try again.
          </p>
        </div>

        <div className="flex flex-col gap-3 w-full">
          <Button type="button" onClick={handleRetry} className="w-full" variant="fill" color="info" size="large">
            Try Again
          </Button>
          <Button type="button" onClick={onClose} className="w-full" variant="fill" color="secondary" size="large">
            Go Back
          </Button>
        </div>
      </div>
    </div>
  );
}

const getSharePageOrigin = () => {
  if (IS_PRODUCTION) {
    return "https://buildblock.ai";
  }

  if (window.location.hostname === "localhost") {
    return "http://localhost:3001";
  }

  return "https://test.buildblock.ai";
};

type DailyLogImage = DailyLogDetailResponse["images"][number];

interface ImageLightboxProps {
  images: DailyLogImage[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

function ImageLightbox({ images, currentIndex, onClose, onNavigate }: ImageLightboxProps) {
  const image = images[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) onNavigate(currentIndex - 1);
      if (e.key === "ArrowRight" && hasNext) onNavigate(currentIndex + 1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, hasPrev, hasNext, onClose, onNavigate]);

  if (!image) return null;

  const src = image.webp_image_upload_path ?? image.image_upload_path;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const SWIPE_THRESHOLD_PX = 50;
    const diff = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (diff > SWIPE_THRESHOLD_PX && hasPrev) onNavigate(currentIndex - 1);
    else if (diff < -SWIPE_THRESHOLD_PX && hasNext) onNavigate(currentIndex + 1);
    touchStartX.current = null;
  };

  return (
    <div
      // z-overlay: BottomSheet과 같은 토큰. rail(z-dropdown)보다 위여야 PC에서 rail이 비치지 않는다.
      className="flex fixed inset-0 z-overlay justify-center items-center bg-black/75"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="absolute inset-0"
        role="button"
        tabIndex={-1}
        aria-label="Close lightbox"
        onClick={onClose}
        onKeyDown={(e) => e.key === "Enter" && onClose()}
      />

      <button type="button" className="absolute top-4 right-4 z-10 p-2 text-white" onClick={onClose} aria-label="Close">
        <X width={24} height={24} />
      </button>

      <span className="absolute top-4 left-1/2 text-white -translate-x-1/2 text-label2">
        {currentIndex + 1} / {images.length}
      </span>

      <div className="flex relative z-10 justify-center items-center px-14 w-full">
        <Image src={src} alt="" className="max-w-full max-h-[85dvh] object-contain" />
      </div>

      {hasPrev && (
        <button
          type="button"
          className="absolute left-1 top-1/2 z-10 p-3 text-white -translate-y-1/2"
          onClick={() => onNavigate(currentIndex - 1)}
          aria-label="Previous image"
        >
          <ChevronLeft width={28} height={28} />
        </button>
      )}

      {hasNext && (
        <button
          type="button"
          className="absolute right-1 top-1/2 z-10 p-3 text-white -translate-y-1/2"
          onClick={() => onNavigate(currentIndex + 1)}
          aria-label="Next image"
        >
          <ChevronRight width={28} height={28} />
        </button>
      )}
    </div>
  );
}

function DailyLogDetailContentBody({
  projectId,
  logId,
  onClose,
  editNavigationState,
  onDeleted,
}: DailyLogDetailContentProps) {
  const { data } = useDailyLogDetailQuery(projectId, logId);
  const {
    data: lockStatus,
    isFetching: isLockStatusFetching,
    isSuccess: isLockStatusSuccess,
  } = useDailyLogLockStatusQuery(projectId, logId);
  const { data: currentUserData } = useCurrentUserQuery();
  const currentUserId = currentUserData?.user.id;
  const isLockedByOther = lockStatus?.locked === true && lockStatus.modifier.id !== currentUserId;
  const { openDeleteDialog } = useDeleteConfirmDialog();
  const comments = useBottomSheet();
  const fileHistory = useBottomSheet();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const photoGridColumns = usePhotoGridColumns();

  useProjectAnalyticsGroup(projectId, data.project_name);

  /** 조회 이벤트를 이미 보낸 로그. 로그 1건당 1회만 발행하기 위한 표시. */
  const reportedViewKeyRef = useRef<string | null>(null);
  /** 락 상태 이벤트를 이미 보낸 로그. */
  const reportedLockStateKeyRef = useRef<string | null>(null);

  // 조회는 화면이 표시되는 즉시 기록한다. 본문은 suspense 로 이미 확보돼 있으므로
  // 비동기 상태를 기다릴 이유가 없다. 기다리면 응답 전에 떠난 방문과 오프라인 방문
  // (쿼리가 일시 중지돼 영영 끝나지 않는다)이 통째로 빠져, 회선이 느린 사용자와 짧은
  // 방문만 선택적으로 누락된다. 본문이 갱신돼도 다시 보내지 않는다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 로그가 바뀔 때만 재발행한다
  useEffect(
    function trackDetailView() {
      const viewKey = `${projectId}:${logId}`;
      if (reportedViewKeyRef.current === viewKey) return;
      reportedViewKeyRef.current = viewKey;

      EventLogger.view(CLIENT_EVENT.DAILY_LOG_DETAIL_VIEW, {
        project_id: projectId,
        log_id: logId,
        image_count: data.images.length,
        tag_count: data.tags.length,
        workforce_count: data.workforces.total_quantity,
        equipment_count: data.equipments.total_quantity,
        description_length: data.description.length,
        is_owner: data.is_owner,
        // 당일 기록인지 몰아서 쓴 과거 기록인지 가른다. 현장 운영 실태를 보는 축.
        days_since_log_date: dayjs().startOf("day").diff(dayjs(data.date).startOf("day"), "day"),
      });
    },
    [projectId, logId],
  );

  /**
   * 편집 경합 노출은 조회와 **별도 이벤트**로 남긴다.
   *
   * 락 상태를 조회 이벤트에 실으면 둘 중 하나를 포기해야 한다 — 확정될 때까지 기다리면
   * 조회수가 누락되고, 기다리지 않으면 미확정 값이 확정값처럼 굳는다. 둘을 나누면
   * 조회는 항상 정확하고, 경합 노출은 확정된 값만 담는다.
   *
   * 확정 조건은 `isSuccess && !isFetching` 이다. 이 쿼리는 `staleTime: 0` 이라 마운트마다
   * 재검증이 도는데, 캐시가 있으면 그동안에도 `isPending` 은 false 다. 그 값을 쓰면
   * "캐시에는 락이 없지만 최신 응답에는 타인의 락이 있는" 경우를 놓친다.
   *
   * 조회에 실패했거나(오프라인 포함) 현재 사용자를 모르면 판정할 수 없으므로 보내지 않는다.
   * 이벤트의 부재가 곧 "확인하지 못함"이고, 실패 자체는 `api_error` 가 따로 집계한다.
   */
  useEffect(
    function trackLockState() {
      if (!isLockStatusSuccess || isLockStatusFetching) return;
      if (currentUserId === undefined) return;

      const lockStateKey = `${projectId}:${logId}`;
      if (reportedLockStateKeyRef.current === lockStateKey) return;
      reportedLockStateKeyRef.current = lockStateKey;

      EventLogger.track(CLIENT_EVENT.DAILY_LOG_LOCK_STATE, {
        project_id: projectId,
        log_id: logId,
        is_locked_by_other: isLockedByOther,
      });
    },
    [projectId, logId, isLockStatusSuccess, isLockStatusFetching, currentUserId, isLockedByOther],
  );

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: dailyLogDetailQueryOptions(projectId, logId).queryKey });
    await queryClient.invalidateQueries({ queryKey: dailyLogLockStatusQueryOptions(projectId, logId).queryKey });
  };
  const { containerRef, pullDistance, isRefreshing, isPulling } = usePullToRefresh({ onRefresh: handleRefresh });

  const parsedLogDate = data.date.trim() ? dayjs(data.date) : null;
  const title = parsedLogDate?.isValid()
    ? `[${parsedLogDate.format("YYYY-MM-DD")}] Daily Construction Report`
    : "Daily Construction Report";

  const { overviewItems, workforceGroups, workforceTotalQuantity, equipmentGroups, equipmentTotalQuantity, infoItems } =
    useDailyLogDisplayData(data);

  const hasAnyResourceSummary = overviewItems.length > 0 || workforceGroups.length > 0 || equipmentGroups.length > 0;
  const hasMilestoneContent = data.description.replace(/<[^>]*>/g, "").trim().length > 0;

  const queryClient = useQueryClient();

  const { mutateAsync: deleteDocument } = useDeleteDocumentMutation({
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: dailyLogDetailQueryOptions(projectId, logId).queryKey });
      queryClient.removeQueries({ queryKey: ["comments", projectId, logId] });
      queryClient.removeQueries({ queryKey: ["file-history", projectId, logId] });
      onDeleted?.();
      onClose();
    },
  });

  const handleDelete = () => {
    openDeleteDialog({
      description: "You will be unable to recover this report.",
      onDelete: () =>
        deleteDocument(
          // 닫기는 mutation의 onSuccess에서 한 번만 한다. 여기서도 닫으면 뒤로 가기가 두 번 실행된다.
          { projectId: Number(projectId), documentId: Number(logId), type: DOCUMENT_TYPE.DAILY_LOG },
        ),
    });
  };

  const navigate = useAppNavigate();

  const { mutate: acquireLock, isPending: isAcquiring } = useAcquireLockMutation(projectId, logId, {
    onSuccess: () => {
      navigate({
        to: "/projects/$projectId/log/$logId/edit",
        params: { projectId, logId },
        state: editNavigationState,
      });
    },
    onError: (error) => {
      const errorCode = isAxiosApiError(error) ? error.response.data.code : null;

      // 동시 편집이 실제로 얼마나 자주 부딪히는지는 제품 결정(락 UX 존치·완화)에 직결되는데,
      // 지금까지는 토스트만 뜨고 데이터에는 아무것도 남지 않았다.
      //
      // 실제 경합(EXISTING_LOCK)일 때만 보낸다. 네트워크·서버·권한 오류까지 싣으면
      // "동시 편집 빈도"가 부풀려지고, 회선이 나쁜 현장일수록 더 부풀려져 결론이 뒤집힌다.
      // 경합이 아닌 실패는 `api_error` 가 엔드포인트 단위로 이미 집계한다.
      if (errorCode === "EXISTING_LOCK") {
        EventLogger.result(CLIENT_EVENT.DAILY_LOG_LOCK_CONFLICT, {
          project_id: projectId,
          log_id: logId,
          source: "edit_click",
          error_code: errorCode,
        });

        queryClient.invalidateQueries({ queryKey: dailyLogLockStatusQueryOptions(projectId, logId).queryKey });
      }
      toast.error("Unable to edit. Someone else is currently editing this report.");
    },
  });

  const handleEditClick = () => {
    if (isLockedByOther || isAcquiring) return;

    const hasOwnLock = lockStatus?.locked === true && lockStatus.modifier.id === currentUserId;

    EventLogger.click(CLIENT_EVENT.DAILY_LOG_EDIT_CLICK, {
      project_id: projectId,
      log_id: logId,
      has_own_lock: hasOwnLock,
    });

    if (hasOwnLock) {
      navigate({
        to: "/projects/$projectId/log/$logId/edit",
        params: { projectId, logId },
        state: editNavigationState,
      });
      return;
    }

    acquireLock();
  };

  const handleOpenComments = () => {
    EventLogger.view(CLIENT_EVENT.COMMENTS_VIEW, { project_id: projectId, log_id: logId });
    comments.open();
  };

  const handleOpenFileHistory = () => {
    EventLogger.view(CLIENT_EVENT.FILE_HISTORY_VIEW, { project_id: projectId, log_id: logId });
    fileHistory.open();
  };

  /**
   * 라이트박스는 열림과 **닫힘 요약**만 남긴다.
   *
   * 스와이프마다 이벤트를 쏘면 사진 30장짜리 로그 하나가 수십 건을 만들어 다른 지표를
   * 파묻는다. "몇 장까지 실제로 봤는가"는 닫을 때 한 건으로 충분히 답할 수 있다.
   */
  const lightboxSessionRef = useRef<{ openedAt: number; seen: Set<number> } | null>(null);

  const handleOpenLightbox = (index: number) => {
    lightboxSessionRef.current = { openedAt: Date.now(), seen: new Set([index]) };
    setLightboxIndex(index);

    EventLogger.view(CLIENT_EVENT.IMAGE_LIGHTBOX_OPEN, {
      project_id: projectId,
      log_id: logId,
      image_count: data.images.length,
      index,
    });
  };

  const handleNavigateLightbox = (index: number) => {
    lightboxSessionRef.current?.seen.add(index);
    setLightboxIndex(index);
  };

  const handleCloseLightbox = () => {
    const session = lightboxSessionRef.current;
    lightboxSessionRef.current = null;
    setLightboxIndex(null);

    if (!session) return;

    EventLogger.track(CLIENT_EVENT.IMAGE_LIGHTBOX_CLOSE, {
      project_id: projectId,
      log_id: logId,
      image_count: data.images.length,
      viewed_count: session.seen.size,
      duration_ms: Date.now() - session.openedAt,
    });
  };

  // 메뉴를 열 때 토큰을 받아 두고, 받기 전에는 Share를 비활성화한다.
  // Safari는 클릭 후 일정 시간이 지나면 공유·복사를 막으므로 공유할 때 토큰을 기다리지 않는다.
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: shareToken } = useDailyLogShareTokenQuery(projectId, logId, { enabled: isMenuOpen });

  const handleShare = useCallback(async () => {
    if (!shareToken) return;

    const shareData = {
      url: `${getSharePageOrigin()}/shared/daily-logs/${projectId}/${logId}?token=${shareToken}`,
    };

    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        return;
      }

      await navigator.clipboard.writeText(shareData.url);
      toast.info("Report link copied to clipboard");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      toast.error("Failed to share report.");
    }
  }, [shareToken, projectId, logId]);

  return (
    <div className="flex flex-col h-dvh relative">
      <header className="flex sticky top-0 z-10 gap-x-2 items-center p-4 md:px-0 bg-gray-10">
        <button type="button" onClick={onClose} aria-label="Close" className="shrink-0">
          <X width={24} height={24} className="text-gray-1000" />
        </button>
        <h1 className="flex-1 min-w-0 truncate text-subtitle4 text-gray-1000">{title}</h1>
        <button type="button" aria-label="Comments" className="p-1 shrink-0" onClick={handleOpenComments}>
          <MessageSquareText width={20} height={20} className="text-gray-900" />
        </button>
        <Dropdown onOpenChange={({ open }) => setIsMenuOpen(open)}>
          <Dropdown.Trigger className="p-1 shrink-0">
            <EllipsisVertical width={20} height={20} className="text-gray-900" />
          </Dropdown.Trigger>
          <Dropdown.Content className="z-20 p-2">
            <Dropdown.Item value="share" onClick={handleShare} disabled={!shareToken}>
              <span
                className={cn(
                  "inline-block px-2 py-3",
                  shareToken ? "cursor-pointer" : "text-gray-400 cursor-not-allowed",
                )}
              >
                Share Report
              </span>
            </Dropdown.Item>
            <Dropdown.Item value="edit" onClick={handleEditClick} disabled={isLockedByOther || isAcquiring}>
              <span
                className={cn(
                  "inline-block px-2 py-3",
                  isLockedByOther || isAcquiring ? "text-gray-400 cursor-not-allowed" : "cursor-pointer",
                )}
              >
                Edit Report
              </span>
            </Dropdown.Item>
            <Dropdown.Item value="history" onClick={handleOpenFileHistory}>
              <span className="inline-block px-2 py-3 cursor-pointer">File History</span>
            </Dropdown.Item>
            {data.is_owner && (
              <Dropdown.Item value="delete" onClick={handleDelete}>
                <span className="inline-block px-2 py-3 text-red-500 cursor-pointer">Delete Report</span>
              </Dropdown.Item>
            )}
          </Dropdown.Content>
        </Dropdown>
      </header>

      {isLockedByOther && lockStatus?.locked && (
        <div className="flex justify-center items-center px-3 py-2 bg-blue-10">
          <p className="flex-1 text-center text-blue-500 text-label3">
            {lockStatus.modifier.name} is currently editing. Read-only mode enabled
          </p>
        </div>
      )}

      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} isPulling={isPulling} />

      <main ref={containerRef} className="flex-1 p-4 overflow-y-auto max-md:bg-white scrollbar-hide">
        <div className="md:bg-white md:rounded-8 md:border md:border-gray-200">
          <div className="mb-8 md:mb-0 md:px-7 md:py-6 md:border-b md:border-gray-200">
            <div className="flex flex-wrap gap-x-4 gap-y-1 justify-between items-start mb-6 md:mb-4">
              <h2 className="text-title6 text-gray-1000">Daily Construction Report</h2>
              <span className="hidden shrink-0 text-gray-600 text-label3 md:inline">
                Last updated {dayjs(data.modified_at).format("MM-DD-YYYY HH:mm:ss")}
              </span>
            </div>

            <ul className="flex flex-col gap-y-4 md:grid md:gap-x-6 md:gap-y-4 md:[grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
              {infoItems.map((item) => (
                // PC에서는 열마다 값 줄 수가 달라도 라벨 높이가 맞도록 위쪽 정렬한다.
                <li key={item.label} className="flex gap-x-4 items-center min-w-0 md:gap-x-3 md:items-start">
                  <img src={item.icon} alt="" className="w-6 h-6 shrink-0 md:mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-gray-600 text-label3">{item.label}</p>
                    {/* 좁은 열에서는 날짜를 쪼개지 않고 D-day 칩을 다음 줄로 넘긴다 */}
                    <div className="flex flex-wrap gap-x-2 gap-y-1 items-center">
                      <p
                        className={cn(
                          "text-body4 md:min-w-0",
                          item.muted ? "font-normal text-gray-500" : "font-medium text-gray-1000",
                        )}
                      >
                        {item.value}
                      </p>
                      {item.chip && (
                        <Chip size="xs" color="info">
                          <span className="text-blue-500">{item.chip}</span>
                        </Chip>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {hasAnyResourceSummary && (
            <div className="mb-6 md:mb-0 md:grid md:grid-cols-2 md:gap-6 md:px-7 md:py-6 md:border-b md:border-gray-200 lg:grid-cols-3">
              <SummarySection title="Overview" items={overviewItems} />

              <ResourceSummarySection
                title="Workforce"
                groups={workforceGroups}
                totalQuantity={workforceTotalQuantity}
              />

              <ResourceSummarySection
                title="Equipment"
                groups={equipmentGroups}
                totalQuantity={equipmentTotalQuantity}
              />
            </div>
          )}

          <section className="mb-6 md:mb-0 md:px-7 md:py-6 md:border-b md:border-gray-200">
            <h3 className="mb-3 text-gray-1000 text-subtitle4">Milestone Report</h3>
            {hasMilestoneContent ? (
              <div
                className={`p-4 max-w-none bg-gray-50 rounded-8 md:p-0 md:max-w-prose md:bg-transparent ${richTextStyles.richTextContent}`}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized by DOMPurify
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.description) }}
              />
            ) : (
              <p className="text-gray-500 text-body3">No milestone notes recorded.</p>
            )}
          </section>

          <section className="md:px-7 md:py-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="flex gap-x-1 items-center text-gray-1000 text-subtitle4">
                <span>Site Picture</span>
                {data.images.length > 0 && (
                  <span className="hidden font-normal text-gray-600 text-label3 md:inline">
                    {data.images.length} photo{data.images.length === 1 ? "" : "s"}
                  </span>
                )}
              </h3>
              {data.images.length > 0 && (
                <DownloadImagesButton projectId={projectId} dailyLogId={logId} imageCount={data.images.length} />
              )}
            </div>
            {data.images.length > 0 ? (
              <VirtualizedImageGrid
                itemCount={data.images.length}
                columns={photoGridColumns}
                scrollElementRef={containerRef}
                getItemKey={(index) => data.images[index]?.id ?? index}
                renderItem={(index) => {
                  const image = data.images[index];
                  if (!image) return null;
                  const src =
                    image.webp_image_upload_path_resized ?? image.webp_image_upload_path ?? image.image_upload_path;
                  return (
                    <button
                      type="button"
                      className="overflow-hidden relative cursor-pointer rounded-8"
                      onClick={() => handleOpenLightbox(index)}
                      aria-label={`View site picture ${index + 1}`}
                    >
                      <Image
                        src={src}
                        alt=""
                        aspectRatio="1/1"
                        className="block object-cover w-full aspect-square"
                        draggable={false}
                      />
                      <span className="absolute w-6 h-6 left-[5px] top-[5px] inline-flex items-center justify-center rounded-full bg-white text-gray-1000 text-caption1">
                        {index + 1}
                      </span>
                    </button>
                  );
                }}
              />
            ) : (
              <p className="text-gray-500 text-body3">No photos uploaded for this report.</p>
            )}
          </section>
        </div>
      </main>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={data.images}
          currentIndex={lightboxIndex}
          onClose={handleCloseLightbox}
          onNavigate={handleNavigateLightbox}
        />
      )}

      <CommentsBottomSheet isOpen={comments.isOpen} onClose={comments.close} projectId={projectId} logId={logId} />
      <FileHistoryBottomSheet
        isOpen={fileHistory.isOpen}
        onClose={fileHistory.close}
        projectId={projectId}
        logId={logId}
      />
    </div>
  );
}

export function DailyLogDetailContent(props: DailyLogDetailContentProps) {
  const { projectId, logId, onClose } = props;
  return (
    <ErrorBoundary
      fallback={({ resetError }) => (
        <DailyLogDetailContentError projectId={projectId} logId={logId} onRetry={resetError} onClose={onClose} />
      )}
    >
      <Suspense fallback={<DailyLogDetailContentSkeleton />}>
        <DailyLogDetailContentBody {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}
